import { Alert, Button, MenuItem, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import { DocumentStatusEnum } from '../../core/types/data-models';
import type { Document } from '../../core/types/data-models';

interface EditDocumentDialogProps {
  open: boolean;
  document: Document;
  onClose: () => void;
  /** Called after the document has been deleted. */
  onDeleted?: (document: Document) => void;
}

const STATUS_OPTIONS: { value: DocumentStatusEnum; label: string }[] = [
  { value: DocumentStatusEnum.DRAFT, label: 'Draft' },
  { value: DocumentStatusEnum.IN_REVIEW, label: 'In Review' },
  { value: DocumentStatusEnum.APPROVED, label: 'Approved' },
  { value: DocumentStatusEnum.ARCHIVED, label: 'Archived' },
];

const IMPORTANCE_OPTIONS = [
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
];

export function EditDocumentDialog({ open, document, onClose, onDeleted }: EditDocumentDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<DocumentStatusEnum>(DocumentStatusEnum.DRAFT);
  const [version, setVersion] = useState('');
  const [importance, setImportance] = useState('MEDIUM');
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(document.name);
    setDescription(document.description ?? '');
    setStatus(document.status);
    setVersion(document.version ?? '');
    setImportance(document.importance ?? 'MEDIUM');
    setError(null);
  }, [open, document]);

  const update = useMutation({
    mutationFn: (payload: Partial<Document>) =>
      resources.documents.update(document.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', document.id] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      onClose();
    },
  });

  const remove = useMutation({
    mutationFn: () => resources.documents.remove(document.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', document.id] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['sidebar-documents'] });
      setConfirmDeleteOpen(false);
      onDeleted?.(document);
      onClose();
    },
    onError: (err) => {
      setConfirmDeleteOpen(false);
      setError(extractErrorMessage(err));
    },
  });

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      await update.mutateAsync({
        name: name.trim(),
        description: description.trim() || null,
        status,
        version: version.trim() || undefined,
        importance,
      });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <>
      <FormDialog
        open={open}
        title="Edit document"
        submitLabel="Save"
        loading={update.isPending}
        onClose={handleClose}
        onSubmit={handleSubmit}
        startAction={
          <Button
            color="warning"
            variant="outlined"
            disabled={update.isPending}
            onClick={() => setConfirmDeleteOpen(true)}
          >
            Delete
          </Button>
        }
      >
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          label="Document name"
          required
          autoFocus
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label="Description"
          fullWidth
          multiline
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          helperText="Optional — brief summary of this document's purpose."
        />
        <TextField
          select
          label="Status"
          fullWidth
          value={status}
          onChange={(e) => setStatus(e.target.value as DocumentStatusEnum)}
        >
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Importance"
          fullWidth
          value={importance}
          onChange={(e) => setImportance(e.target.value)}
        >
          {IMPORTANCE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Version"
          fullWidth
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          helperText="e.g. 1.0, 2.1"
        />
      </FormDialog>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete document?"
        message={`Are you sure you want to delete "${document.name}"? This will permanently delete the document and all related sections, visualizations, comments, and other associated data. This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDeleteOpen(false)}
      />
    </>
  );
}
